/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-nested-ternary */
import * as Yup from 'yup';
import { OBLIGATION_REGEX, CONTACT_NUMBER_REGEX, NAME_FIELDS_NEW_REGEX, ORGANISATION_NAME_REGEX, VEHICLE_REGISTRATION_REGEX, FIELDS_WITH_SEPCIFI_CHARS } from '@services/ValidationRules';
import { iAddress, iContact, iFormMetaData, iSummary, iSummaryField } from '@interfaces/interfaces';
import { ProgressStep, FormType } from '@constants';
import { FormikValues } from 'formik';
import { INVALID_OBLIGATION_NUMBER, INVALID_NUMERIC_FORMAT, INVALID_NAME_FORMAT, EMPTY_DATE, INVALID_FUTURE_DATE, INVALID_PHONE_NUMBER_LENGTH, INVALID_ORG_NAME_FORMAT, INVALID_PHONE_NUMBER, INVALID_VEHICLE_REGISTRATION_NUMBER, INVALID_EMAIL, MISSING_DECLARATION_AGREEMENT, FIELDS_WITH_SEPCIFI_CHARS_MSG, INVALID_PHONE_NUMBER_LENGTH_15 } from './ErrorMessages';
import keys from './PageValidationKeys';

const joinAddressFields = (entry: string[]) => {
  if (entry[0] === 'Address' || entry[0] === 'SearchText') return null;
  if (entry[0] === 'Country') return entry[1];
  return entry[1];
};

export interface iFormData {
  a: string;
  user: {
    UserType: string;
    title: string;
    other: string;
    givenName: string;
    familyName: string;
    dateOfBirth: {
      day: string;
      month: string;
      year: string;
    };
    licence: {
      number: string;
      state: string;
      isInternational: boolean;
      country: string;
    };
    vehicleRegos: [
      {
        number: string;
      },
    ];
    postalAddress: {
      line1: string;
      line2: string;
      suburb: string;
      state: string;
      postcode: string;
      country: string;
    };
    email: string;
    noEmail: boolean;
    residentialAddress: {
      hasDifferentResidentialAddress: boolean;
      line1: string;
      line2: string;
      suburb: string;
      state: string;
      postcode: string;
      country: string;
    };
    primaryContact: {
      type: string;
      number: string;
      minlength: string;
      maxlength: string;
    };
    secondaryContact: {
      type: string;
      number: string;
    };
    sendCopyTo: string;
  };
  company: {
    companyPosition: {
      position: string;
      other: string;
    };
    companyName: string;
    abn: string;
    acn: string;
    postalAddress: {
      line1: string;
      line2: string;
      suburb: string;
      state: string;
      postcode: string;
      country: string;
    };
    residentialAddress: {
      hasDifferentResidentialAddress: boolean;
      line1: string;
      line2: string;
      suburb: string;
      state: string;
      postcode: string;
      country: string;
    };
    option: string;
  };
  sendcopy: boolean;
}

interface iObligation {
  ID: string;
  ObligationNO: string;
  IsPrimary: boolean;
}

export interface iCommonCourtFormValues {
  [key: string]: string | number | boolean | iFormData | iObligation[] | string[] | Date | null | iAddress | iContact;
  // Court Step1 Field
  OBLIGATION_NUMBER: string;
  // Court Step4 response
  InfringementNoticeNumber: string;
  // ContactInformation Step 5 fields
  TitleMrMrsMsMissOther: string;
  FamilyName: string;
  GivenNames: string;
  Email: string;
  EMAIL_TO: string;
  // step 6 company court variation
  PositionOfThePersonWithinTheCompany: string;
  CompanyName: string;
  AbnacnType: string;
  AbnNo: string;
  AcnNo: string;
  StatecountryOfIssue: string;
}

export interface iCourtFormValues extends iCommonCourtFormValues {
  // Court Step1 Field
  OBLIGATION_NUMBER: string;
  // Court Step2 Fields
  OFFENCE_DATE: Date | null;
  HEARING_DATE: Date | null;
  DRIVER_LICENCE: string;
  VEHICLE_REGISTRATION: string;
  // SetupDebtor response
  OffenceDateRequired: boolean;
  HearingDateRequired: boolean;
  LicenceRequired: boolean;
  RegistrationRequired: boolean;
  DOBRequired: boolean;
  // Court step 3 fields
  SaveYesNo: string;
  SaveOptions: string;
  saveEmail: string;
  saveMobile: string;
  // CheckingExistingForm response
  PartialFormID: number;
  CheckingExisting: string;
  PartialFormExpiryDate: string;
  // GetPartialForm response
  ID: number;
  FormData: iFormData;
  Obligations: iObligation[];
  // Court Step4 response
  InfringementNoticeNumber: string;
  Attachments: string[];
  DebtorID: number;
  FormTypeID: number;
  IsSubscribedEM: boolean;
  IsSubscribedSMS: boolean;
  EMRemeinderSent: boolean;
  SMSRemeinderSent: boolean;
  CreateDate: string;
  ExpiryDate: string;
  // Court Step6 Fields
  CompanyAddress: iAddress;
  HasDifferentCompanyAddress: string;
  CompanyPostalAddress: iAddress;
  OtherPosition: string;
  // GetObligationOptions response
  DebtorType: string;
  // GetBasicInfo response
  SeriousObligation: boolean;
  DDD: boolean;
  ESI: boolean;
  MarineOffence: boolean;
  // CreateReceiptNumber response
  submissionNo: string;
  // ContactInformation Step 5 fields
  DOB: Date | null;
  LicenceState: string;
  NoDriverLicence: boolean;
  ResidentialAddress: iAddress;
  SummonsAddress: iAddress;
  HasDifferentResidentialAddress: string;
  CourtContactNumber: iContact;
  Email: string;
  CourtContactNumberWarning: string;
  CourtContactNumberWarningSec: string;
  EmailWarning: string;
  NoEmail: boolean;
  // Court Step 10
  declaration: boolean;
  intend: string;
  submissionError: string;
}

export interface iSubmitCourtFormValues extends iCommonCourtFormValues {
  YourObligationNumber: string;
  IdentifyingReference: string;
  TitleMrMrsMsMissOther: string;
  GivenNames: string;
  FamilyName: string;
  DD: string;
  MM: string;
  YYYY: string;
  DriverLicenceNumber: string;
  StatecountryOfIssue: string;
  CompanyName: string;
  AbnacnType: string;
  AbnacnNo: string;
  PositionOfThePersonWithinTheCompany: string;
  Email: string;
  ContactNumber: string;
  NumberType: string;
  SecondaryContactNumber: string;
  NumberTypeTwo: string;
  LineOne: string;
  LineTwo: string;
  Suburb: string;
  State: string;
  Postcode: string;
  Country: string;
  LineOneTwo: string;
  LineTwoTwo: string;
  SuburbTwo: string;
  StateTwo: string;
  PostcodeTwo: string;
  CountryTwo: string;
  LineOneThree: string;
  LineTwoThree: string;
  SuburbThree: string;
  StateThree: string;
  PostcodeThree: string;
  CountryThree: string;
  LineOneFour: string;
  LineTwoFour: string;
  SuburbFour: string;
  StateFour: string;
  PostcodeFour: string;
  CountryFour: string;
  GroupOneZero: string;
  DdTwo: number;
  MmTwo: number;
  YyyyTwo: number;
  YourSignature: string;
  EMAIL_TO: string;
  Intent: string;
  FooterSubmissionNo1: string;
  FooterFormType1: string;
  FooterObligationNo1: string;
  FooterSubmittedTime1: string;
  FooterSubmissionNo2: string;
  FooterFormType2: string;
  FooterObligationNo2: string;
  FooterSubmittedTime2: string;
  FooterSubmissionNo3: string;
  FooterFormType3: string;
  FooterObligationNo3: string;
  FooterSubmittedTime3: string;
  FooterSubmissionNo4: string;
  FooterFormType4: string;
  FooterObligationNo4: string;
  FooterSubmittedTime4: string;
  FooterSubmissionNo5: string;
  FooterFormType5: string;
  FooterObligationNo5: string;
  FooterSubmittedTime5: string;
  EmailAddressOptional: string;
}

export const defaultForm = (): iCourtFormValues => {
  const defaultValues: iCourtFormValues = {
    // iCommonValues
    OBLIGATION_NUMBER: '',
    InfringementNoticeNumber: '',
    // Court step 3 fields
    SaveYesNo: '',
    SaveOptions: '',
    saveEmail: '',
    saveMobile: '',
    // iCourtFormValues
    // Court Step2 Fields
    OFFENCE_DATE: null,
    HEARING_DATE: null,
    DRIVER_LICENCE: '',
    VEHICLE_REGISTRATION: '',
    // SetupDebtor response
    OffenceDateRequired: false,
    HearingDateRequired: false,
    LicenceRequired: false,
    RegistrationRequired: false,
    DOBRequired: false,
    // GetObligationOptions response
    DebtorType: '',
    // GetBasicInfo response
    SeriousObligation: false,
    DDD: false,
    ESI: false,
    MarineOffence: false,
    // CheckingExistingForm response
    PartialFormID: 0,
    CheckingExisting: '',
    PartialFormExpiryDate: '',
    // GetPartialForm response
    ID: 0,
    FormData: {
      a: '',
      user: {
        UserType: 'Individual',
        title: '',
        other: '',
        givenName: '',
        familyName: '',
        dateOfBirth: {
          day: '',
          month: '',
          year: '',
        },
        licence: {
          number: '',
          state: '',
          isInternational: false,
          country: '',
        },
        vehicleRegos: [
          {
            number: '',
          },
        ],
        postalAddress: {
          line1: '',
          line2: '',
          suburb: '',
          state: '',
          postcode: '',
          country: 'Australia',
        },
        email: '',
        noEmail: false,
        residentialAddress: {
          hasDifferentResidentialAddress: false,
          line1: '',
          line2: '',
          suburb: '',
          state: '',
          postcode: '',
          country: 'Australia',
        },
        primaryContact: {
          type: 'Mobile',
          number: '',
          minlength: '',
          maxlength: '',
        },
        secondaryContact: {
          type: 'Mobile',
          number: '',
        },
        sendCopyTo: '',
      },
      company: {
        companyPosition: {
          position: '',
          other: '',
        },
        companyName: '',
        acn: '',
        abn: '',
        postalAddress: {
          line1: '',
          line2: '',
          suburb: '',
          state: '',
          postcode: '',
          country: '',
        },
        residentialAddress: {
          hasDifferentResidentialAddress: false,
          line1: '',
          line2: '',
          suburb: '',
          state: '',
          postcode: '',
          country: '',
        },
        option: '',
      },
      sendcopy: false,
    },
    Obligations: [
      {
        ID: '',
        ObligationNO: '',
        IsPrimary: false,
      },
    ],
    Attachments: [],
    DebtorID: 0,
    FormTypeID: 0,
    TitleMrMrsMsMissOther: '',
    FamilyName: '',
    GivenNames: '',
    StatecountryOfIssue: 'Australia',
    Email: '',
    EMAIL_TO: '',
    NoEmail: false,
    DOB: null,
    LicenceState: '',
    NoDriverLicence: false,
    HasDifferentResidentialAddress: '',
    ResidentialAddress: {
      Address: '',
      LineOne: '',
      LineTwo: '',
      Suburb: '',
      State: '',
      Postcode: '',
      Country: 'Australia',
    },
    SummonsAddress: {
      Address: '',
      LineOne: '',
      LineTwo: '',
      Suburb: '',
      State: '',
      Postcode: '',
      Country: 'Australia',
    },
    CourtContactNumber: {
      ContactNumber: '',
      NumberType: '',
      SecondaryContactNumber: '',
      SecondaryNumberType: '',
    },
    CourtContactNumberWarningSec: '',
    CourtContactNumberWarning: '',
    EmailWarning: '',
    // step 6 company court variation
    PositionOfThePersonWithinTheCompany: '',
    OtherPosition: '',
    CompanyName: '',
    AbnacnType: '',
    AbnNo: '',
    AcnNo: '',
    CompanyAddress: {
      Address: '',
      LineOne: '',
      LineTwo: '',
      Suburb: '',
      State: '',
      Postcode: '',
      Country: 'Australia',
    },
    HasDifferentCompanyAddress: '',
    CompanyPostalAddress: {
      Address: '',
      LineOne: '',
      LineTwo: '',
      Suburb: '',
      State: '',
      Postcode: '',
      Country: 'Australia',
    },
    // CreateReceiptNumber response
    submissionNo: '',
    IsSubscribedEM: false,
    IsSubscribedSMS: false,
    EMRemeinderSent: false,
    SMSRemeinderSent: false,
    CreateDate: '',
    ExpiryDate: '',
    // Court Step 10
    declaration: false,
    intend: '',
    submissionError: ''
  };
  return defaultValues;
};

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
export const getReceiptPayload = (formValues: any): string => `{
    "ID": "",
    "Receipt": "",
    "Obligations": [
        {
            "ID": "",
            "ObligationNO": "${formValues.OBLIGATION_NUMBER}",
            "IsPrimary": "true",
            "ReceptNumberId": ""
        }
    ],
    "SubmittedDate": "",
    "FormTypeId": 5
  }`;

export const getSubmissionPayload = (formValues: iCourtFormValues, submissionNumber: string): iSubmitCourtFormValues => {
  const submitFormValues = formValues;
  const now = new Date();
  const MONTH = now.getMonth() + 1;
  const DATE = now.getDate();
  const YEAR = now.getFullYear();
  const MINUTE = (now.getMinutes() < 10 ? '0' : '') + now.getMinutes();
  const SUBMITTED_TIME = `${now.getHours()}:${MINUTE} ${DATE}/${MONTH}/${YEAR}`;
  const FORM_TYPE = 'Court';

  const submitObject: iSubmitCourtFormValues = {
    ...(submitFormValues as iCommonCourtFormValues),
    YourObligationNumber: formValues.OBLIGATION_NUMBER,
    IdentifyingReference: formValues.OBLIGATION_NUMBER,
    TitleMrMrsMsMissOther: formValues.TitleMrMrsMsMissOther,
    GivenNames: formValues.GivenNames,
    FamilyName: formValues.FamilyName,
    AbnacnNo: formValues.AbnNo ? formValues.AbnNo : formValues.AcnNo,
    DD: formValues.DOB ? formValues.DOB.getDate().toString() : '',
    MM: formValues.DOB ? (formValues.DOB.getMonth() + 1).toString() : '',
    YYYY: formValues.DOB ? formValues.DOB.getFullYear().toString() : '',
    ContactNumber: formValues.CourtContactNumber.ContactNumber,
    NumberType: formValues.CourtContactNumber.NumberType,
    SecondaryContactNumber: formValues.CourtContactNumber.SecondaryContactNumber,
    NumberTypeTwo: formValues.CourtContactNumber.SecondaryContactNumber ? formValues.CourtContactNumber.SecondaryNumberType : '',
    LineOne: formValues.HasDifferentResidentialAddress === 'true' ? formValues.ResidentialAddress.LineOne : formValues.SummonsAddress.LineOne,
    LineTwo: formValues.HasDifferentResidentialAddress === 'true' ? formValues.ResidentialAddress.LineTwo : formValues.SummonsAddress.LineTwo,
    Suburb: formValues.HasDifferentResidentialAddress === 'true' ? formValues.ResidentialAddress.Suburb : formValues.SummonsAddress.Suburb,
    State: formValues.HasDifferentResidentialAddress === 'true' ? formValues.ResidentialAddress.State : formValues.SummonsAddress.State,
    Postcode: formValues.HasDifferentResidentialAddress === 'true' ? formValues.ResidentialAddress.Postcode : formValues.SummonsAddress.Postcode,
    Country: formValues.SummonsAddress.LineOne ? (formValues.HasDifferentResidentialAddress === 'true' ? formValues.ResidentialAddress.Country : formValues.SummonsAddress.Country) : '',
    LineOneTwo: formValues.SummonsAddress.LineOne,
    LineTwoTwo: formValues.SummonsAddress.LineTwo,
    SuburbTwo: formValues.SummonsAddress.Suburb,
    StateTwo: formValues.SummonsAddress.State,
    PostcodeTwo: formValues.SummonsAddress.Postcode,
    CountryTwo: formValues.SummonsAddress.LineOne ? formValues.SummonsAddress.Country : '',
    LineOneThree: formValues.CompanyAddress.LineOne,
    LineTwoThree: formValues.CompanyAddress.LineTwo,
    SuburbThree: formValues.CompanyAddress.Suburb,
    StateThree: formValues.CompanyName ? formValues.CompanyAddress.State : '',
    PostcodeThree: formValues.CompanyAddress.Postcode,
    CountryThree: formValues.CompanyName ? formValues.CompanyAddress.Country : '',
    LineOneFour: formValues.HasDifferentCompanyAddress === 'true' ? formValues.CompanyPostalAddress.LineOne : formValues.CompanyAddress.LineOne,
    LineTwoFour: formValues.HasDifferentCompanyAddress === 'true' ? formValues.CompanyPostalAddress.LineTwo : formValues.CompanyAddress.LineTwo,
    SuburbFour: formValues.HasDifferentCompanyAddress === 'true' ? formValues.CompanyPostalAddress.Suburb : formValues.CompanyAddress.Suburb,
    // not sure how to combine with conditional for CompanyName (look at court.dist.js)
    StateFour: formValues.HasDifferentCompanyAddress === 'true' ? formValues.CompanyPostalAddress.State : formValues.CompanyAddress.State,
    PostcodeFour: formValues.HasDifferentCompanyAddress === 'true' ? formValues.CompanyPostalAddress.Postcode : formValues.CompanyAddress.Postcode,
    CountryFour: formValues.CompanyName ? (formValues.HasDifferentCompanyAddress === 'true' ? formValues.CompanyPostalAddress.Country : formValues.CompanyAddress.Country) : '',
    GroupOneZero: 'false',
    DdTwo: DATE,
    MmTwo: MONTH,
    YyyyTwo: YEAR,
    Email: formValues.NoEmail ? '' : formValues.Email,
    DriverLicenceNumber: formValues.NoDriverLicence ? '' : formValues.DRIVER_LICENCE,
    StatecountryOfIssue: formValues.NoDriverLicence ? '' : (formValues.StatecountryOfIssue === 'Australia' ? formValues.LicenceState : formValues.StatecountryOfIssue),
    EMAIL_TO: formValues.EMAIL_TO === 'true' ? formValues.Email : '',
    YourSignature: formValues.declaration ? 'I agree to the statements' : '',
    Intent: formValues.intend || 'I do not',
    FooterSubmissionNo1: submissionNumber,
    FooterFormType1: FORM_TYPE,
    FooterObligationNo1: formValues.OBLIGATION_NUMBER,
    FooterSubmittedTime1: SUBMITTED_TIME,
    FooterSubmissionNo2: submissionNumber,
    FooterFormType2: FORM_TYPE,
    FooterObligationNo2: formValues.OBLIGATION_NUMBER,
    FooterSubmittedTime2: SUBMITTED_TIME,
    FooterSubmissionNo3: submissionNumber,
    FooterFormType3: FORM_TYPE,
    FooterObligationNo3: formValues.OBLIGATION_NUMBER,
    FooterSubmittedTime3: SUBMITTED_TIME,
    FooterSubmissionNo4: submissionNumber,
    FooterFormType4: FORM_TYPE,
    FooterObligationNo4: formValues.OBLIGATION_NUMBER,
    FooterSubmittedTime4: SUBMITTED_TIME,
    FooterSubmissionNo5: submissionNumber,
    FooterFormType5: FORM_TYPE,
    FooterObligationNo5: formValues.OBLIGATION_NUMBER,
    FooterSubmittedTime5: SUBMITTED_TIME,
    EmailAddressOptional: formValues.NoEmailAddress ? '' : formValues.Email,
  };

  const excludeKeysFromForm: string[] = [
    // fields from iCourtFormValues
    'OffenceDateRequired',
    'HearingDateRequired',
    'LicenceRequired',
    'RegistrationRequired',
    'DOBRequired',
    'OFFENCE_DATE',
    'HEARING_DATE',
    'DRIVER_LICENCE',
    'PartialFormID',
    'ID',
    'FormData',
    'Obligations',
    'Attachments',
    'DebtorID',
    'FormTypeID',
    'IsSubscribedEM',
    'IsSubscribedSMS',
    'EMRemeinderSent',
    'SMSRemeinderSent',
    'CreateDate',
    'ExpiryDate',
    'SeriousObligation',
    'DDD',
    'ESI',
    'MarineOffence',
    'submissionNo',
    'DOB',
    'LicenceState',
    'ResidentialAddress',
    'HasDifferentResidentialAddress',
    'SummonsAddress',
    'CourtContactNumber',
    'Email',
    'NoEmail',
    'declaration',
    'intend',
    'SaveYesNo',
    'SaveOptions',
    'saveEmail',
    'saveMobile',
    'a',
    'VEHICLE_REGISTRATION',
    'UserType',
    'NoDriverLicence',
    'CompanyAddress',
    'CompanyPostalAddress',
    'HasDifferentCompanyAddress',
    'DebtorType',
    'AbnNo',
    'AcnNo',
    'CheckingExisting',
    'PartialFormExpiryDate',
    'submissionError',
    'CourtContactNumberWarning',
    'CourtContactNumberWarningSec',
    'EmailWarning',
  ];
  excludeKeysFromForm.forEach((value: string) => {
    delete submitObject[value];
  });
  return submitObject;
};

export const CourtSchema = Yup.object().shape({
  saveEmail: Yup.string().email(INVALID_EMAIL).required(INVALID_NUMERIC_FORMAT),
  saveMobile: Yup.string().max(10, INVALID_PHONE_NUMBER_LENGTH).matches(CONTACT_NUMBER_REGEX, INVALID_PHONE_NUMBER).required(INVALID_PHONE_NUMBER),
  ResidentialAddress: Yup.object().shape({
    LineOne: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    LineTwo: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG),
    Suburb: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    State: Yup.string().required(INVALID_NUMERIC_FORMAT),
    Postcode: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    Country: Yup.string().required(INVALID_NUMERIC_FORMAT),
  }),
  SummonsAddress: Yup.object().shape({
    LineOne: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    LineTwo: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG),
    Suburb: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    State: Yup.string().required(INVALID_NUMERIC_FORMAT),
    Postcode: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    Country: Yup.string().required(INVALID_NUMERIC_FORMAT),
  }),
  CompanyAddress: Yup.object().shape({
    LineOne: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    LineTwo: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG),
    Suburb: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    State: Yup.string().required(INVALID_NUMERIC_FORMAT),
    Postcode: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    Country: Yup.string().required(INVALID_NUMERIC_FORMAT),
  }),
  CompanyPostalAddress: Yup.object().shape({
    LineOne: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    LineTwo: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG),
    Suburb: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    State: Yup.string().required(INVALID_NUMERIC_FORMAT),
    Postcode: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
    Country: Yup.string().required(INVALID_NUMERIC_FORMAT),
  }),
  OBLIGATION_NUMBER: Yup.string().matches(OBLIGATION_REGEX, INVALID_OBLIGATION_NUMBER).required(INVALID_NUMERIC_FORMAT),
  OFFENCE_DATE: Yup.date().required(EMPTY_DATE).nullable().max(new Date(), INVALID_FUTURE_DATE),
  HEARING_DATE: Yup.date().required(EMPTY_DATE).nullable(),
  DRIVER_LICENCE: Yup.string().matches(FIELDS_WITH_SEPCIFI_CHARS, FIELDS_WITH_SEPCIFI_CHARS_MSG).required(INVALID_NUMERIC_FORMAT),
  VEHICLE_REGISTRATION: Yup.string().matches(VEHICLE_REGISTRATION_REGEX, INVALID_VEHICLE_REGISTRATION_NUMBER).required(INVALID_VEHICLE_REGISTRATION_NUMBER),
  TitleMrMrsMsMissOther: Yup.string().required(INVALID_NUMERIC_FORMAT),
  DOB: Yup.date().required(EMPTY_DATE).nullable().max(new Date(), INVALID_FUTURE_DATE),
  FamilyName: Yup.string().matches(NAME_FIELDS_NEW_REGEX, INVALID_NAME_FORMAT).required(INVALID_NUMERIC_FORMAT),
  GivenNames: Yup.string().matches(NAME_FIELDS_NEW_REGEX, INVALID_NAME_FORMAT).required(INVALID_NUMERIC_FORMAT),
  StatecountryOfIssue: Yup.string(),
  LicenceState: Yup.string().required(INVALID_NUMERIC_FORMAT),
  NoDriverLicence: Yup.boolean(),
  PositionOfThePersonWithinTheCompany: Yup.string().required(INVALID_NUMERIC_FORMAT),
  OtherPosition: Yup.string().required(INVALID_NUMERIC_FORMAT),
  CompanyName: Yup.string().max(80).matches(ORGANISATION_NAME_REGEX, INVALID_ORG_NAME_FORMAT).required(INVALID_NUMERIC_FORMAT),
  AbnacnType: Yup.string().required(INVALID_NUMERIC_FORMAT),
  AbnNo: Yup.string().required(INVALID_NUMERIC_FORMAT),
  AcnNo: Yup.string().required(INVALID_NUMERIC_FORMAT),
  Email: Yup.string().nullable().email(INVALID_EMAIL).required(INVALID_NUMERIC_FORMAT),
  NoEmail: Yup.boolean(),
  EMAIL_TO: Yup.string().required(INVALID_NUMERIC_FORMAT),
  CourtContactNumber: Yup.object().shape({
    ContactNumber: Yup.string().max(15, INVALID_PHONE_NUMBER_LENGTH_15).matches(CONTACT_NUMBER_REGEX, INVALID_PHONE_NUMBER).required(INVALID_PHONE_NUMBER),
    NumberType: Yup.string(),
    SecondaryContactNumber: Yup.string().max(15, INVALID_PHONE_NUMBER_LENGTH_15).matches(CONTACT_NUMBER_REGEX, INVALID_PHONE_NUMBER),
    SecondaryNumberType: Yup.string(),
  }),
  HasDifferentResidentialAddress: Yup.string().required(INVALID_NUMERIC_FORMAT),
  HasDifferentCompanyAddress: Yup.string().required(INVALID_NUMERIC_FORMAT),
  intend: Yup.string().required(INVALID_NUMERIC_FORMAT),
  declaration: Yup.boolean().oneOf([true], MISSING_DECLARATION_AGREEMENT),
  SaveYesNo: Yup.string().required(INVALID_NUMERIC_FORMAT),
});

const { COURT } = FormType;

export const courtMetaData: iFormMetaData = {
  pagesSegments: {
    step0: {
      progressStep: ProgressStep.START,
      validationKeys: (values: FormikValues) => [],
    },
    step1: {
      progressStep: ProgressStep.START,
      validationKeys: (values: FormikValues) => keys(values, COURT).page1,
    },
    step2: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => keys(values, COURT).page2,
    },
    step3: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => keys(values, COURT).page3,
    },
    step4: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => [],
    },
    step5: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => keys(values, COURT).page5,
    },
    step6: {
      progressStep: ProgressStep.DETAILS,
      validationKeys: (values: FormikValues) => keys(values, COURT).page6,
    },
    step8: {
      progressStep: ProgressStep.DONE,
      validationKeys: (values: FormikValues) => [],
    },
    step9: {
      progressStep: ProgressStep.DONE,
      validationKeys: (values: FormikValues) => [],
    },
    step10: {
      progressStep: ProgressStep.CHECK_CONFIRM,
      validationKeys: (values: FormikValues) => [],
    },
    step11: {
      progressStep: ProgressStep.CHECK_CONFIRM,
      validationKeys: (values: FormikValues) => keys(values, COURT).page11,
    },
    step12: {
      progressStep: ProgressStep.DONE,
      validationKeys: (values: FormikValues) => [],
    },
  },
};

export const getSummaryScreen = (values: iCourtFormValues): iSummary[] => {
  const page4 = {
    title: 'Your fines',
    editLink: '/step4',
    fields: [
      [
        {
          title: 'Fines to include in application',
          value: values.OBLIGATION_NUMBER,
        },
      ]
    ],
  };
  const page5 = {
    title: 'Your details',
    editLink: '/step5',
    fields: [
      [{ title: 'Title', value: values.TitleMrMrsMsMissOther }],
      [{ title: 'Given name(s)', value: values.GivenNames }],
      [{ title: 'Family name', value: values.FamilyName }],
      [{ title: 'Date of birth', value: values.DOB ? `${values.DOB.getDate()}/${values.DOB.getMonth() + 1}/${values.DOB.getFullYear()}` : '' }],
      values.NoDriverLicence ? [{ title: "Driver's licence", value: 'n/a' }] : [{ title: "Driver's licence", value: values.DRIVER_LICENCE }],
      values.NoDriverLicence
        ? [{ title: 'Place of issue', value: 'n/a' }]
        : [{ title: 'Place of issue', value: values.StatecountryOfIssue === 'Australia' ? values.LicenceState : values.StatecountryOfIssue }],
      [
        {
          title: 'Address for service of summons',
          value: `${Object.entries(values.SummonsAddress)
            .map((entry) => joinAddressFields(entry))
            .filter(Boolean)
            .join(', ')}`,
        },
      ],
      values.HasDifferentResidentialAddress === 'false'
        ? [{ title: 'Is your residential address different to your address for service of summons?', value: 'No' }]
        : [
            {
              title: 'Residential address',
              value: `${Object.entries(values.ResidentialAddress)
                .map((entry) => joinAddressFields(entry))
                .filter(Boolean)
                .join(', ')}`,
            },
          ],
          [{ title: 'Contact phone number', value: values.CourtContactNumber.ContactNumber }],
          [{ title: 'Number type', value: values.CourtContactNumber.NumberType }],
          [
            {
              title: 'Secondary contact number',
              value: values.CourtContactNumber.SecondaryContactNumber ? values.CourtContactNumber.SecondaryContactNumber : 'n/a',
            },
          ],
          [{ title: 'Secondary number type', value: values.CourtContactNumber.SecondaryContactNumber ? values.CourtContactNumber.SecondaryNumberType : 'n/a' }],
      [{ title: 'Email', value: values.Email ? values.Email : 'n/a' }],
      [{ title: 'Would you like to receive a copy of the completed application at the email address provided?', value: values.EMAIL_TO === 'true' ? 'Yes' : 'No' }],
    ],
  };

  const page6 = {
    title: 'Your contact information',
    editLink: '/step6',
    fields: [
      [{ title: 'Title', value: values.TitleMrMrsMsMissOther }],
      [{ title: 'Given name(s)', value: values.GivenNames }],
      [{ title: 'Family name', value: values.FamilyName }],
      [{ title: 'Position in the company', value: values.PositionOfThePersonWithinTheCompany }],
      [{ title: 'Date of birth', value: values.DOB ? `${values.DOB.getDate()}/${values.DOB.getMonth() + 1}/${values.DOB.getFullYear()}` : '' }],
      values.NoDriverLicence ? [{ title: "Driver's licence", value: 'n/a' }] : [{ title: "Driver's licence", value: values.DRIVER_LICENCE }],
      values.NoDriverLicence
        ? [{ title: 'Place of issue', value: 'n/a' }]
        : [{ title: 'Place of issue', value: values.StatecountryOfIssue === 'Australia' ? values.LicenceState : values.StatecountryOfIssue }],
      [{ title: 'Company Name', value: values.CompanyName }],
      values.AbnacnType === 'ABN'
        ? [{ title: 'ABN', value: values.AbnNo }]
        : [] as iSummaryField[],
      values.AbnacnType === 'ACN'
        ? [{ title: 'ACN', value: values.AcnNo }]
        : [] as iSummaryField[],
      [
        {
          title: 'Company\'s street address (Court summons will be sent here)',
          value: `${Object.entries(values.CompanyAddress)
            .map((entry) => joinAddressFields(entry))
            .filter(Boolean)
            .join(', ')}`,
        },
      ],
      values.HasDifferentCompanyAddress === 'false'
        ? [{ title: 'Is your postal address different to your company\'s address?', value: 'No' }]
        : [
            {
              title: 'Postal address',
              value: `${Object.entries(values.CompanyPostalAddress)
                .map((entry) => joinAddressFields(entry))
                .filter(Boolean)
                .join(', ')}`,
            },
          ],
          [{ title: 'Contact phone number', value: values.CourtContactNumber.ContactNumber }],
          [{ title: 'Number type', value: values.CourtContactNumber.NumberType }],
          [
            {
              title: 'Secondary contact number',
              value: values.CourtContactNumber.SecondaryContactNumber ? values.CourtContactNumber.SecondaryContactNumber : 'n/a',
            },
          ],
          [{ title: 'Secondary number type', value: values.CourtContactNumber.SecondaryContactNumber ? values.CourtContactNumber.SecondaryNumberType : 'n/a' }],
      [{ title: 'Email', value: values.Email ? values.Email : 'n/a' }],
      [{ title: 'Would you like to receive a copy of the completed application at the email address provided?', value: values.EMAIL_TO === 'true' ? 'Yes' : 'No' }],
    ],
  };

  const summarySections: iSummary[] = [
    page4,
    values.DebtorType === 'Individual' ? page5 : {} as iSummary, values.DebtorType === 'Company' ? page6 : {} as iSummary];
  return summarySections;
};
